
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useImpact } from '../context/ImpactContext';
import {
    Box,
    Typography,
    Grid,
    Button,
    Container,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import SectionWrapper from './SectionWrapper';

const normalizeSectionTitle = (raw) => {
    const input = (raw || '').trim();
    if (!input) return input;

    // If content came in as all-caps, render it in a more editorial Title Case.
    const isAllCaps = input === input.toLocaleUpperCase('pt-BR') && /[A-ZÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ]/i.test(input);
    if (!isAllCaps) return input;

    const small = new Set(['e', 'de', 'da', 'do', 'das', 'dos', 'em', 'por', 'para', 'a', 'o']);
    const lower = input.toLocaleLowerCase('pt-BR');

    return lower
        .split(/\s+/)
        .map((w, i) => {
            if (w === '&') return '&';
            if (i !== 0 && small.has(w)) return w;
            return w.charAt(0).toLocaleUpperCase('pt-BR') + w.slice(1);
        })
        .join(' ');
};

const TuringDiagram = () => {
    // A clean, original SVG inspired by early Turing-machine state diagrams.
    // Keep it abstract to avoid copying any specific published diagram 1:1.
    return (
        <Box
            component="svg"
            viewBox="0 0 640 520"
            role="img"
            aria-label="Diagrama de estados inspirado em Máquina de Turing"
            sx={{
                width: '100%',
                height: '100%',
                // Keep it present, but slightly faded so it reads as an ambient layer.
                opacity: 0.82,
                transition: 'opacity 220ms ease, filter 220ms ease',
                filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.06))',
                // Styling tokens
                '& .node': {
                    fill: 'rgba(24,24,24,0.10)',
                    stroke: 'rgba(255,255,255,0.40)',
                    strokeWidth: 2,
                },
                '& .nodeLabel': {
                    fill: 'rgba(255,255,255,0.70)',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 16,
                    fontWeight: 700,
                },
                '& .edge': {
                    fill: 'none',
                    stroke: 'rgba(255,255,255,0.24)',
                    strokeWidth: 2,
                    strokeLinecap: 'round',
                },
                // Subtle "proof of craft": a faint trace that moves along the edges.
                '& .edgeFx': {
                    fill: 'none',
                    stroke: 'rgba(255,255,255,0.55)',
                    strokeWidth: 1.5,
                    strokeLinecap: 'round',
                    strokeDasharray: '2 22',
                    animation: 'turingTrace 8.5s linear infinite',
                    opacity: 0.18,
                },
                '& .edgeLabel': {
                    fill: 'rgba(255,255,255,0.50)',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 12,
                    letterSpacing: '0.04em',
                },
                '& .caption': {
                    fill: 'rgba(255,255,255,0.42)',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 11,
                    letterSpacing: '0.14em',
                },
                '@keyframes turingTrace': {
                    '0%': { strokeDashoffset: 0, opacity: 0.10 },
                    '15%': { opacity: 0.22 },
                    '85%': { opacity: 0.22 },
                    '100%': { strokeDashoffset: -320, opacity: 0.10 },
                },
                '@media (prefers-reduced-motion: reduce)': {
                    '& .edgeFx': { animation: 'none', opacity: 0.10 },
                },
            }}
        >
            <defs>
                <marker id="arrow" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                    <path d="M0,0 L10,5 L0,10 z" fill="rgba(255,255,255,0.28)" />
                </marker>
            </defs>

            {/* Edges (base) */}
            <path className="edge" markerEnd="url(#arrow)" d="M152 120 C 250 70, 330 70, 430 120" />
            <path className="edge" markerEnd="url(#arrow)" d="M430 156 C 430 220, 430 246, 430 300" />
            <path className="edge" markerEnd="url(#arrow)" d="M410 322 C 320 380, 260 392, 182 360" />
            <path className="edge" markerEnd="url(#arrow)" d="M150 156 C 150 220, 150 246, 150 300" />
            <path className="edge" markerEnd="url(#arrow)" d="M170 322 C 250 390, 310 420, 430 440" />
            <path className="edge" markerEnd="url(#arrow)" d="M450 440 C 520 452, 560 424, 560 360 C 560 300, 520 272, 452 300" />

            {/* Edge effects (no arrowheads; keep it subtle) */}
            <path className="edgeFx" d="M152 120 C 250 70, 330 70, 430 120" />
            <path className="edgeFx" d="M430 156 C 430 220, 430 246, 430 300" />
            <path className="edgeFx" d="M410 322 C 320 380, 260 392, 182 360" />
            <path className="edgeFx" d="M150 156 C 150 220, 150 246, 150 300" />
            <path className="edgeFx" d="M170 322 C 250 390, 310 420, 430 440" />
            <path className="edgeFx" d="M450 440 C 520 452, 560 424, 560 360 C 560 300, 520 272, 452 300" />

            {/* Edge labels (abstract transition triples) */}
            <text className="edgeLabel" x="255" y="70" textAnchor="middle">
                (a, A, R)
            </text>
            <text className="edgeLabel" x="452" y="230" textAnchor="start">
                (b, B, L)
            </text>
            <text className="edgeLabel" x="300" y="392" textAnchor="middle">
                (A, A, R)
            </text>
            <text className="edgeLabel" x="118" y="230" textAnchor="end">
                (B, B, R)
            </text>

            {/* Nodes */}
            <circle className="node" cx="150" cy="138" r="36" />
            <circle className="node" cx="430" cy="138" r="36" />
            <circle className="node" cx="430" cy="318" r="36" />
            <circle className="node" cx="150" cy="318" r="36" />
            <circle className="node" cx="430" cy="448" r="36" />

            {/* Node labels */}
            <text className="nodeLabel" x="150" y="144" textAnchor="middle">
                q0
            </text>
            <text className="nodeLabel" x="430" y="144" textAnchor="middle">
                q1
            </text>
            <text className="nodeLabel" x="430" y="324" textAnchor="middle">
                q2
            </text>
            <text className="nodeLabel" x="150" y="324" textAnchor="middle">
                q3
            </text>
            <text className="nodeLabel" x="430" y="454" textAnchor="middle">
                q4
            </text>

            {/* Caption */}
            <text className="caption" x="320" y="500" textAnchor="middle">
                TURING-MACHINE STATE SKETCH
            </text>
        </Box>
    );
};

// ASSETS
// Link to public assets is now handled by string paths inside the component



const ImpactSection = () => {
    const { impactData } = useImpact();
    const sectionTitle = normalizeSectionTitle(impactData.sectionTitle) || 'Ciência & Tecnologia';

    // CLOCK STATE
    const [time, setTime] = useState(new Date());

    // EFFECT: Clock Tick
    React.useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 100);
        return () => clearInterval(timer);
    }, []);

    return (
        <SectionWrapper
            id="ciencia-tecnologia"
            sx={{
                bgcolor: '#181818', // Technical Charcoal (Softened)
                color: '#fff',
                overflow: 'hidden'
            }}
        >
            <Box
                component="div"
                sx={{
                    width: '100%',
                    // height: '100%', // Managed by flex content below
                    minHeight: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    borderTop: '1px solid #222'
                }}
            >
                {/* BACKGROUND GRID (Static) */}
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
                    backgroundSize: '100px 100px',
                    pointerEvents: 'none'
                }} />

                {/* --- DATA DENSITY OVERLAYS (HUD) --- */}

                {/* Top-Left System Status */}
                <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 10, display: { xs: 'none', md: 'block' } }}>
                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6rem', color: '#444' }}>
                        SYS_STATUS: ONLINE<br />
                        LAT: -15.7684 | LON: -47.8642<br />
                        BUFFER: 1024MB | T_SYNC: AUTO
                    </Typography>
                </Box>

                {/* Bottom-Right Versioning */}
                <Box sx={{ position: 'absolute', bottom: 20, right: 20, zIndex: 10, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6rem', color: '#444' }}>
                        GENESIS_CYCLE: V7.3.0 [DEEP_REALISM] :: 0x{time.getMilliseconds().toString(16).toUpperCase().padStart(3, '0')}<br />
                        R5_IMM.ENGINE: ACTIVE [PROCESSING_CONTEXT]<br />
                        SECURE_CONNECTION: ESTABLISHED_
                    </Typography>
                </Box>

                {/* --- TOP HALF: CONTEXT ENGINE (55%) --- */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #222', // Subtle divider
                    position: 'relative',
                    px: { xs: 4, md: 6 },
                    pt: { xs: 6, md: 6 }, // Reduced from 10 to minimize top gap
                    pb: { xs: 4, md: 4 }, // Reduced from 6
                    height: '100%',
                    justifyContent: 'center', // Back to center
                    boxSizing: 'border-box',
                    flex: 1,      // KEY: Flex Grow
                    minHeight: 0, // KEY: Allow shrink
                    flexDirection: 'column'
                }}>
                    {/* Quadrant Markers */}
                    <Box sx={{ position: 'absolute', top: 0, left: '50%', height: 10, width: 1, bgcolor: '#333' }} />
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', height: 10, width: 1, bgcolor: '#333' }} />

                    <Container maxWidth="xl" sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                        <Grid container spacing={0} sx={{ flex: 1, height: '100%', minHeight: 0 }}>
                            {/* LEFT COLUMN: TEXT CONTENT */}
                            <Grid size={{ xs: 12, md: 6 }} sx={{
                                p: { xs: 4, md: 4 }, // Reduced from 8 to save vertical space
                                pr: { md: 6 },       // Keep generous right padding for readability
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center', // Vertically Center content (Safe now with compact text)
                                borderRight: '1px solid #333',
                                position: 'relative'
                            }}>
                                {/* ... Content ... */}
                                <Box sx={{ position: 'relative', zIndex: 2 }}>
                                    <Box sx={{
                                        mb: 2, // Reduced margin to fit headline
                                        borderBottom: '1px solid #333',
                                        pb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%'
                                    }}>
	                                        <Typography
	                                            variant="h2"
	                                            sx={{
	                                                fontFamily: '"Inter", sans-serif',
	                                                fontSize: 'clamp(1.05rem, 0.55vw + 0.75rem, 1.35rem)',
	                                                letterSpacing: '-0.02em',
	                                                fontWeight: 800,
	                                                textTransform: 'none',
	                                                lineHeight: 1.05,
	                                                color: '#fff',
	                                                display: 'flex',
	                                                // Cursor-like marker: align to text base (not vertical center).
	                                                alignItems: 'flex-end',
	                                                textRendering: 'geometricPrecision',
	                                                WebkitFontSmoothing: 'antialiased',
	                                            }}
	                                        >
	                                            {sectionTitle}
	                                            <Box component="span" sx={{
		                                                display: 'inline-block',
		                                                width: 10,
		                                                height: 10,
		                                                borderRadius: '2px',
		                                                bgcolor: '#CC0000',
		                                                ml: 1.5,
		                                                flex: '0 0 auto',
		                                                mb: '0.12em',
		                                                boxShadow: '0 0 18px rgba(204,0,0,0.25)',
		                                            }} />
	                                        </Typography>
                                    </Box>

	                                    <Typography
	                                        variant="h3"
	                                        sx={{
	                                            fontFamily: '"Inter", sans-serif',
	                                            fontSize: { xs: 'clamp(1.75rem, 5.2vmin, 2.25rem)', md: 'clamp(2.0rem, 2.2vmin, 2.6rem)' },
	                                            fontWeight: 800,
	                                            color: '#fff',
	                                            lineHeight: 1.06,
	                                            mb: 1,
	                                            maxWidth: '650px',
	                                            letterSpacing: '-0.03em',
	                                        }}
	                                    >
	                                        {impactData.title || "Pesquisa & Inovação"}
	                                    </Typography>

                                    <Box sx={{
                                        pl: 2,
                                        borderLeft: '2px solid #333',
                                        mb: 2 // Minimized gap
                                    }}>
                                        <Typography variant="body1" sx={{
                                            color: '#ccc',
                                            maxWidth: '650px', // Slightly wider to accommodate larger text
                                            fontSize: { xs: '1.10rem', md: '1.25rem' }, // Increased font size significantly (was 1.15rem)
                                            lineHeight: 1.6,
                                            // Removed LineClamp to use available vertical space
                                        }}>
	                                            {impactData.description || "Integrada ao ecossistema de Ciência, Tecnologia e Inovação da Universidade de Brasília (UnB), a R5 dispõe de capacidade técnico-científica ampliada, que permite desenvolver projetos com elevado grau de complexidade e customização. Com metodologia própria, fundamentada na abordagem de Design Science e Gestão da Inovação, a R5 desenvolve e implementa soluções tecnológicas e modelos avançados de suporte à decisão, para enfrentar problemas complexos em diferentes contextos organizacionais. Dessa forma, transformamos desafios estratégicos em soluções de alto valor agregado e enorme potencial de retorno."}
	                                        </Typography>
                                    </Box>

                                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                                        <Button
                                            component={RouterLink}
                                            to="/proposito"
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{
                                                borderRadius: '14px',
                                                border: '1px solid #fff',
                                                color: '#000',
                                                bgcolor: '#fff',
                                                fontFamily: '"JetBrains Mono", monospace',
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                padding: '10px 24px',
                                                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                                '&:hover': {
                                                    transform: 'scale(1.04)',
                                                    bgcolor: '#CC0000',
                                                    color: '#fff',
                                                    borderColor: '#CC0000',
                                                    boxShadow: '0 0 20px rgba(204, 0, 0, 0.35)'
                                                }
                                            }}
                                        >
                                            Propósito e Missão
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
	                            <Grid size={{ xs: 12, md: 6 }} sx={{
	                                height: '100%',
	                                p: { xs: 4, md: 6 },
	                                pt: { xs: 2, md: 4 },
	                                display: 'flex',
	                                justifyContent: 'center',
	                                alignItems: 'center',
	                                position: 'relative',
	                                overflow: 'hidden',
	                                borderLeft: '1px solid #333',
	                            }}>
	 
		                                <Box
		                                    sx={{
		                                        position: 'relative',
		                                        zIndex: 1,
		                                        width: '100%',
		                                        // Give the SVG an explicit box so it can't collapse to 0px height.
		                                        maxWidth: 520,
		                                        aspectRatio: '640 / 520',
		                                        // Prevent it from overpowering the section on short viewports.
		                                        maxHeight: { xs: 320, md: 420 },
		                                        display: 'flex',
		                                        alignItems: 'center',
		                                        justifyContent: 'center',
		                                        // Keep it from overpowering the text column.
		                                        opacity: 0.88,
		                                        transition: 'opacity 220ms ease',
		                                        '&:hover': { opacity: 0.98 },
		                                    }}
		                                >
		                                    <TuringDiagram />
		                                </Box>
	 
	                            </Grid>
	                        </Grid>
	                    </Container>
	                </Box>


            </Box>
        </SectionWrapper >
    );
};

export default ImpactSection;
